/**
 * Role predicates, mirroring `core/permissions.py`.
 *
 * The API is the authority — every one of these rules is enforced server-side, and the
 * client copy exists only so the UI does not offer a control that would be refused.
 * When these two disagree, the backend wins and this file is the bug.
 */
import type { Role } from "@/api/types";

const FINANCE_ROLES: readonly Role[] = ["moliyachi", "owner"];
const REPORTING_ROLES: readonly Role[] = ["branch_manager", "moliyachi", "owner"];
const CRM_WRITE_ROLES: readonly Role[] = ["operator", "branch_manager", "owner"];

export function canViewKassaBalances(role: Role | null | undefined): boolean {
  if (role === null || role === undefined) {
    return false;
  }
  return FINANCE_ROLES.includes(role);
}

export function canManageExpenses(role: Role | null | undefined): boolean {
  if (role === null || role === undefined) {
    return false;
  }
  return FINANCE_ROLES.includes(role);
}

export function canApprovePayments(role: Role | null | undefined): boolean {
  if (role === null || role === undefined) {
    return false;
  }
  return FINANCE_ROLES.includes(role);
}

export function canViewReports(role: Role | null | undefined): boolean {
  if (role === null || role === undefined) {
    return false;
  }
  return REPORTING_ROLES.includes(role);
}

/** The monthly CustDev archive (TZ §8.6): the owner alone, as `CanViewCustDev` on the API. */
export function canViewCustDev(role: Role | null | undefined): boolean {
  return role === "owner";
}


/** Open the AI agent section at all. Moliyachi has no part in it; operators see only the posts. */
export function canOpenAiAgent(role: Role | null | undefined): boolean {
  if (role === null || role === undefined) {
    return false;
  }
  return role !== "moliyachi";
}

export function canOverrideDiscipline(role: Role | null | undefined): boolean {
  if (role === null || role === undefined) {
    return false;
  }
  return role === "owner";
}

/**
 * Open `Suhbatlar` (the inbox). Mirrors `INBOX_ROLES` in `core/realtime.py` and the
 * `CanUseInbox` permission: Egasi, Menejer and Operator. Moliyachi has no inbox.
 */
const INBOX_ROLES: readonly Role[] = ["owner", "branch_manager", "operator"];

export function canUseInbox(role: Role | null | undefined): boolean {
  if (role === null || role === undefined) {
    return false;
  }
  return INBOX_ROLES.includes(role);
}

/**
 * Open `Sozlamalar` at all. Mirrors `can_manage_tenant_settings`.
 *
 * The whole screen is owner-only: every section on it writes tenant configuration, so
 * there is nothing left for another role to look at once the controls are refused.
 */
export function canManageTenantSettings(role: Role | null | undefined): boolean {
  if (role === null || role === undefined) {
    return false;
  }
  return role === "owner";
}

/** Confirming or rejecting a supplier's place link: the owner and a branch manager. */
export function canResolveProviderPlaces(role: Role | null | undefined): boolean {
  if (role === null || role === undefined) {
    return false;
  }
  return role === "owner" || role === "branch_manager";
}

/** Confirming or splitting a supplier hotel the matcher could not decide (D167): the same people. */
export function canResolveHotelMatches(role: Role | null | undefined): boolean {
  return canResolveProviderPlaces(role);
}

/** Marking a hotel as halal for the agency (D169 addendum): the owner and a branch manager. */
export function canMarkHalal(role: Role | null | undefined): boolean {
  return canResolveProviderPlaces(role);
}

/** Writing the agency's own tour packages (D174): the owner and a branch manager. */
export function canManagePackages(role: Role | null | undefined): boolean {
  return canResolveProviderPlaces(role);
}

/**
 * Bronlar (§10): the operators' bookings and messages are sales work, so every role but the
 * moliyachi opens them. The moliyachi sees what the agency owes an operator in Moliya instead.
 */
export function canOpenBookings(role: Role | null | undefined): boolean {
  return role !== null && role !== undefined && role !== "moliyachi";
}

/** Operator-entered payments wait for approval; a finance role books directly. */
export function paymentStartsPendingApproval(role: Role | null | undefined): boolean {
  if (role === null || role === undefined) {
    return false;
  }
  return !FINANCE_ROLES.includes(role);
}

/**
 * Create, change or delete a deal, client or traveller.
 *
 * A moliyachi is deliberately absent. They need to read the pipeline — approving a
 * payment means opening the deal it belongs to — but they never move it. The prototype
 * hid `Bitimlar` and `Mijozlar` from them altogether, which was the error: the screens
 * stay and the write controls go.
 */
export function canEditCrmRecords(role: Role | null | undefined): boolean {
  if (role === null || role === undefined) {
    return false;
  }
  return CRM_WRITE_ROLES.includes(role);
}

/**
 * Reading the agency's contract wording (TZ §17.14 Shablonlar, D455). Mirrors
 * `core.permissions.can_view_contract_templates`: whoever may issue a contract reads the
 * template they are signing against. Writing it is `canManageContractTemplates`.
 */
export function canViewContractTemplates(role: Role | null | undefined): boolean {
  if (role === null || role === undefined) {
    return false;
  }
  return CRM_WRITE_ROLES.includes(role);
}

/**
 * Curating the agency's destinations (TZ §17.14 Yo'nalishlar, §1.4). Mirrors
 * `core.permissions.can_manage_destinations`: the owner and a branch manager.
 */
export function canManageDestinations(role: Role | null | undefined): boolean {
  return role === "owner" || role === "branch_manager";
}

/** Changing the agency's contract wording: the owner alone, as on the API. */
export function canManageContractTemplates(role: Role | null | undefined): boolean {
  return role === "owner";
}

/** Switching automation rules on, off or editing them (Q69, D215): the owner alone, as on the API. */
export function canManageAutomations(role: Role | null | undefined): boolean {
  return role === "owner";
}

/** Sending a segment campaign is a bulk send (D215): the owner and the branch manager, as on the API. */
export function canRunCampaigns(role: Role | null | undefined): boolean {
  return role === "owner" || role === "branch_manager";
}

/**
 * Jamoa va filiallar and Taqsimlash qoidalari (spec §2, §11.3): the owner, and the branch manager
 * for their own branch. The screens' data is branch-scoped by the server.
 */
export function canManageTeamAndDistribution(role: Role | null | undefined): boolean {
  return role === "owner" || role === "branch_manager";
}

/** §8 "Excelga yuklash": the whole client base leaves the product through the owner only (9.47). */
export function canExportClients(role: Role | null | undefined): boolean {
  return role === "owner";
}

/** §8 bulk Telegram message: the owner and the branch manager, as `can_send_bulk` (9.50). */
export function canSendClientBulk(role: Role | null | undefined): boolean {
  return role === "owner" || role === "branch_manager";
}

/** §8 bulk owner change: `can_assign_client`, the owner anywhere and a manager in their branch. */
export function canReassignClients(role: Role | null | undefined): boolean {
  return role === "owner" || role === "branch_manager";
}
